import { useTranslations } from 'next-intl'
import { Dispatch, MutableRefObject, SetStateAction } from 'react'

import { showNotification } from '@/components/Notification'

import {
	TimeoutError,
	withTimeoutError,
} from '../../../../utils/withTimeoutError'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../../contentFormSections'

export const useValidateContentForm = ({
	formRefs,
	formSectionList,
	setOpenSectionList,
}: {
	formRefs: MutableRefObject<
		Record<
			string,
			{
				order: number
				ref: TContentFormHandle
			}
		>
	>
	formSectionList: TQrContentFormSection[]
	setOpenSectionList: Dispatch<SetStateAction<string[]>>
}) => {
	const t = useTranslations()

	return async () => {
		async function validateAllSections<T>(
			array: T[],
			callback: (item: T, index: number) => Promise<boolean>,
		): Promise<boolean> {
			const results = await Promise.all(
				array.map((item, index) => callback(item, index)),
			)
			return results.every((result) => result)
		}

		const orderedFormRefList = Object.values(formRefs.current).sort(
			(a, b) => a.order - b.order,
		)

		try {
			return await withTimeoutError(
				validateAllSections(orderedFormRefList, async ({ order, ref }) => {
					const { isValid } = await ref.validate()
					if (!isValid) {
						const invalidFormSectionId = formSectionList?.[order]?.id
						if (invalidFormSectionId) {
							setOpenSectionList((prev) => [...prev, invalidFormSectionId])
						}
					}
					return isValid
				}),
				2_000,
			)
		} catch (error) {
			if (error instanceof TimeoutError) {
				void showNotification(t('public__generic__unexpected_error'), {
					type: 'error',
					context: 'generator',
				})
			} else {
				console.error('Validation error:', error)
			}
			return false
		}
	}
}
